import { BodyText, Button } from '@epilot/journey-elements'
import type {
  BlockConfiguration,
  BlockMappingData,
  PriceItem
} from '@epilot/journey-logic-commons'
import {
  getPriceDisplayInJourneys,
  priceItemHasComponents,
  sortItemRecurrenceDetails,
  getAmountDetails
} from '@epilot/journey-logic-commons'
import type { TFunction } from 'i18next'
import { useMemo, useState } from 'react'

import { ExpandIcon } from '../../../Product/ProductExpandIcon'

import { CartItemDetails } from './CartItemDetails'
import { CartItemImage } from './CartItemImage'
import { useCartItemStyles } from './styles'

type CartItemProps = {
  item: PriceItem & {
    blockConfiguration?: BlockConfiguration
    blockMappingData?: BlockMappingData
  }
  t: TFunction
  displayPriceComponents?: boolean
  displayUnitaryAverage?: boolean
  showTrailingDecimalZeros?: boolean
  forceExpandedItemDetails?: boolean
}

export const CartItem = ({
  item,
  t,
  displayPriceComponents,
  displayUnitaryAverage,
  showTrailingDecimalZeros,
  forceExpandedItemDetails
}: CartItemProps) => {
  const classes = useCartItemStyles()
  const [isExpanded, setIsExpanded] = useState(
    forceExpandedItemDetails || false
  )
  const priceDisplayInJourneys = getPriceDisplayInJourneys(item)

  const shouldDisplayDetails = useMemo(() => {
    if (priceItemHasComponents(item)) {
      return (
        displayPriceComponents &&
        priceDisplayInJourneys !== 'show_as_on_request'
      )
    }

    return (
      priceDisplayInJourneys !== 'show_as_on_request' &&
      item._price?.variable_price &&
      item.blockMappingData?.numberInput !== undefined
    )
  }, [displayPriceComponents, item, priceDisplayInJourneys])

  const sortedRecurrenceDetails = sortItemRecurrenceDetails(item)

  const hasOnlyOnetimeRecurrences = sortedRecurrenceDetails.every(
    (recurrence) => recurrence.type === 'one_time' || !recurrence.type
  )

  const {
    amountToDisplay,
    discountAmountToDisplay,
    beforeDiscountAmountTotalToDisplay,
    priceType
  } = useMemo(
    () =>
      getAmountDetails({
        showTrailingDecimalZeros,
        t,
        currency: item.currency,
        sortedRecurrenceDetails,
        priceDisplayInJourneys,
        hasOnlyOnetimeRecurrences
      }),
    [
      hasOnlyOnetimeRecurrences,
      item.currency,
      priceDisplayInJourneys,
      showTrailingDecimalZeros,
      sortedRecurrenceDetails,
      t
    ]
  )

  return (
    <>
      <div
        className={classes.cartItem}
        data-testid={`cart-item-${item._product?._id}:${item._price?._id}`}
      >
        <div className={classes.cartItemImageSection}>
          <CartItemImage product={item._product} />
        </div>
        <div className={classes.cartItemDescriptionSection}>
          <BodyText
            className={classes.productName}
            color="textPrimary"
            display="block"
            title={item._product?.name}
          >
            <strong>{item._product?.name}</strong>
          </BodyText>
          <BodyText
            className={classes.priceDescription}
            color="textSecondary"
            title={item._price?.description}
          >
            {item._price?.description}
          </BodyText>
        </div>
        <div className={classes.cartItemAmountSection}>
          <div className={'row1'}>
            {shouldDisplayDetails && (
              <OpenComponentsButton
                isExpanded={isExpanded}
                setIsExpanded={setIsExpanded}
              />
            )}
            <BodyText color="textPrimary" data-testid="cart-item-amount">
              <strong>{amountToDisplay}</strong>
            </BodyText>
          </div>
          <div className={classes.discountInnerContainer}>
            {beforeDiscountAmountTotalToDisplay !== undefined && (
              <BodyText
                className={classes.priceBeforeDiscount}
                color="textSecondary"
                display="block"
              >
                {beforeDiscountAmountTotalToDisplay}
              </BodyText>
            )}
            {discountAmountToDisplay !== undefined && (
              <BodyText
                className={classes.discount}
                color="textSecondary"
                display="block"
              >
                -{discountAmountToDisplay}
              </BodyText>
            )}
          </div>
          {typeof priceType !== 'undefined' && (
            <BodyText color="textSecondary" display="block">
              {priceType}
            </BodyText>
          )}
        </div>
      </div>
      <CartItemDetails
        componentDetails={sortedRecurrenceDetails}
        displayPriceComponents={displayPriceComponents}
        displayUnitaryAverage={displayUnitaryAverage}
        hasOnlyOnetimeRecurrences={hasOnlyOnetimeRecurrences}
        isExpanded={isExpanded}
        item={item}
        showTrailingDecimalZeros={showTrailingDecimalZeros}
      />
    </>
  )
}

function OpenComponentsButton({
  isExpanded,
  setIsExpanded
}: {
  isExpanded: boolean
  setIsExpanded: (value: boolean) => void
}) {
  const classes = useCartItemStyles()

  return (
    <Button
      className={classes.openComponentsButton}
      data-testid={'cart-collapse-button'}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <ExpandIcon expanded={isExpanded} />
    </Button>
  )
}
